import config from './config';

type ImageSize = 'xs' | 'sm' | 'md' | 'lg';

const PUBLIC_DOCS_IMAGE_ORIGINS = [
    'https://assets.kalshi.com',
    'https://kalshi-public-docs.s3.amazonaws.com',
    'https://kalshi-public-docs.s3.us-east-1.amazonaws.com',
];

/** Same-origin path that proxies to the images CloudFront origin via a
 * Next.js rewrite (see `next.config.js`). Browser-bound image URLs are
 * rewritten to live under this prefix so they pick up the year-long
 * `Cache-Control` header we attach to it — repeats serve from cache
 * instantly (no reload blink). Replaced artwork is picked up because the
 * backend hands us a versioned series URL (`?v=<hash>`, see
 * `getSeriesImageUrl`), which is a new cache key; only the ticker-derived
 * fallback still relies on `stale-while-revalidate`. */
const CDN_IMAGES_PROXY_PREFIX = '/cdn-images';

/** Same-origin proxy prefix for the public images S3 bucket. */
const CDN_PUBLIC_IMAGES_PROXY_PREFIX = '/cdn-public-images';

/**
 * Rewrites an absolute images-CloudFront URL to the same-origin
 * `/cdn-images/...` proxy path. No-op for any URL that isn't pointed at the
 * configured CloudFront origin (e.g. raw S3 paths, local assets, fully
 * relative URLs already pointing at the proxy).
 *
 * Use this only for URLs that will be fetched by the browser — server-side
 * URL generation (OG images, share cards, route handlers that serve image
 * responses) should keep using the absolute CloudFront URL since `fetch()`
 * in those contexts cannot resolve a same-origin path.
 */
export const proxyCdnImageUrl = (url: string): string => {
    if (!url) return url;
    const cdnOrigin = config.kalshiImagesCloudfrontUrl;
    if (!url.startsWith(cdnOrigin)) return url;
    return `${CDN_IMAGES_PROXY_PREFIX}${url.slice(cdnOrigin.length)}`;
};

/** Rewrites an absolute public-images-S3 URL to the same-origin `/cdn-public-images/...` proxy path. No-op for any other origin. */
export const proxyPublicImageUrl = (url: string): string => {
    if (!url) return url;
    const origin = config.kalshiPublicImagesS3BucketUrl;
    if (!url.startsWith(origin)) return url;
    return `${CDN_PUBLIC_IMAGES_PROXY_PREFIX}${url.slice(origin.length)}`;
};

/**
 * Converts an S3 image URL to its CDN equivalent.
 * Replaces the S3 origin with the CloudFront CDN URL and appends a `?size` param when provided.
 * Safe to call on non-S3 URLs — they are returned unchanged.
 */
export const toCdnImageUrl = (url: string, size?: ImageSize): string => {
    const publicDocsOrigin = PUBLIC_DOCS_IMAGE_ORIGINS.find((origin) => url === origin || url.startsWith(`${origin}/`));
    const cdnUrl = publicDocsOrigin ? url.replace(publicDocsOrigin, config.kalshiImagesCloudfrontUrl) : url;
    if (!size) return cdnUrl;
    // Skip size param for .avif images — the CDN resizer doesn't support AVIF and returns 502
    if (cdnUrl.includes('.avif')) return cdnUrl;
    const separator = cdnUrl.includes('?') ? '&' : '?';
    return `${cdnUrl}${separator}size=${size}`;
};

/**
 * True when `src` cannot be served through Next's image optimizer and has to be fetched
 * straight from its origin instead.
 *
 * `next.config.js` pins `search: ''` on every local and remote image pattern, so no caller can
 * mint unbounded optimizer cache keys by varying a query nonce. The cost is that a legitimate
 * query-bearing URL — a `?size=` CDN variant, a backend `custom_image_url` carrying a `?v=`
 * cache-buster — is no longer routable: in production `/_next/image` answers 400 and the tile
 * renders broken, and in development next/image's default loader throws outright (E426/E231).
 *
 * `OptimizedImage` applies this itself. Any component handing a dynamic, backend- or
 * CDN-derived URL straight to `next/image` must pass the result as `unoptimized`, which
 * short-circuits the loader entirely and emits the src verbatim — no layout change.
 *
 * `data:` sources carry their bytes inline and never reach the optimizer boundary.
 */
export const requiresImageOptimizerBypass = (url: string | null | undefined): boolean =>
    !!url && !/^data:/i.test(url) && url.includes('?');

/**
 * Normalizes an image URL into a stable key for equality comparison: routes through
 * the CDN and strips any query string so cache-busting / size params don't produce
 * false negatives when comparing two visually identical images.
 */
export const normalizeImageKey = (url: string | null | undefined): string | null => {
    if (!url) return null;
    const cdn = toCdnImageUrl(url);
    const qIdx = cdn.indexOf('?');
    return qIdx === -1 ? cdn : cdn.slice(0, qIdx);
};

/**
 * Returns true when every item in `items` resolves (via `getItemImageUrl`) to the
 * same image URL as `headerImageUrl`. Used to detect that the "header" image and
 * all per-item images are duplicates and at least one of them can be suppressed.
 *
 * Guards:
 *   - Empty items -> false
 *   - Missing/empty header image -> false
 *   - Any item with no resolvable image -> false (don't hide placeholders that
 *     aren't actually duplicates)
 */
export const allImagesMatchHeader = <T>({
    items,
    headerImageUrl,
    getItemImageUrl,
}: {
    items: readonly T[];
    headerImageUrl: string | null | undefined;
    getItemImageUrl: (item: T) => string | null | undefined;
}): boolean => {
    if (items.length === 0) return false;
    const headerKey = normalizeImageKey(headerImageUrl);
    if (!headerKey) return false;
    return items.every((item) => {
        const itemKey = normalizeImageKey(getItemImageUrl(item));
        return !!itemKey && itemKey === headerKey;
    });
};

const imageQueryParams = (imageSize?: ImageSize): string => (!!imageSize ? `?size=${imageSize}` : '');

// TODO: Public images should be moved to the public docs bucket ASAP,
// as that's where we've elected to store series images and have enabled dynamic
// sizing. For now dynamic sizing *will not* work in this bucket.
/**
 * Resolves a public-images-S3 asset URL.
 *
 * Pass `{ absolute: true }` when the URL will be consumed outside the main
 * app's Next.js host (e.g. the widget-app, which has no `/cdn-public-images`
 * rewrite), or in any CSS `url()` / inline style that runs in such a context.
 * Browser callers inside the main app should use the default (proxied) form.
 */
export const getPublicImage = (
    imageName: string,
    imageSize?: ImageSize,
    { absolute = false }: { absolute?: boolean } = {},
): string => {
    const absoluteUrl = `${config.kalshiPublicImagesS3BucketUrl}/${imageName}`;
    return (absolute ? absoluteUrl : proxyPublicImageUrl(absoluteUrl)) + imageQueryParams(imageSize);
};

/**
 * Resolves the display image URL for an event or card.
 * Prefers product_metadata.custom_image_url when set (backend-controlled override),
 * then the API-supplied series image URL (`seriesImageUrl`, versioned for cache
 * busting), and only then the series URL constructed from the ticker.
 */
export const getEventImageUrl = ({
    seriesTicker,
    customImageUrl,
    seriesImageUrl,
}: {
    seriesTicker?: string;
    customImageUrl?: string;
    seriesImageUrl?: string | null;
}): string => {
    if (customImageUrl) return customImageUrl;
    return getSeriesImageUrl({ seriesTicker, seriesImageUrl });
};

/**
 * Resolves a series' artwork URL at the requested CDN size.
 *
 * Prefers the API-supplied URL (`SeriesVo.image_url`, or the `image_url` key the
 * BFF/search endpoints merge into `product_metadata`). The backend versions it
 * (`...webp?v=<hash>`) whenever artwork is re-uploaded, so routing it through the
 * resizing CDN yields a new cache key and browsers/Vercel refetch instead of
 * serving the year-long-cached old image. Falls back to the ticker-derived URL
 * for callers that have no series data — identical to the unversioned image.
 */
export const getSeriesImageUrl = ({
    seriesTicker,
    seriesImageUrl,
    size = 'sm',
}: {
    seriesTicker?: string;
    seriesImageUrl?: string | null;
    size?: ImageSize;
}): string => {
    if (typeof seriesImageUrl === 'string' && seriesImageUrl) {
        // Category/commodity filter endpoints already hand back sized CDN URLs
        // (`?size=sm&v=...`); don't append a second `size`.
        return /[?&]size=/.test(seriesImageUrl) ? toCdnImageUrl(seriesImageUrl) : toCdnImageUrl(seriesImageUrl, size);
    }
    if (seriesTicker) return getSeriesImageSrc(seriesTicker, size);
    return '';
};

/**
 * Ticker-derived series artwork URL. This is the *fallback* for callers with no
 * series data: it is not versioned, so a replaced image at the same path stays
 * cached until `stale-while-revalidate` catches up. Prefer `getSeriesImageUrl`
 * with the API's `image_url` wherever a series/card/search payload is in scope.
 */
export const getSeriesImageSrc = (seriesTicker: string, imageSize: ImageSize = 'sm'): string =>
    config.kalshiImagesCloudfrontUrl + '/series-images-webp/' + seriesTicker + '.webp' + imageQueryParams(imageSize);

export const getNextImageUrl = (imgUrl: string, width = 1920, quality = 80): string => {
    const baseURL = window.location.protocol + '//' + window.location.host;
    return `${baseURL}/_next/image?url=${imgUrl}&w=${width}&q=${quality}`;
};

export const compressImage = (base64String: string, targetWidth: number, targetHeight: number, _quality: number) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Unable to obtain 2D canvas context'));
                return;
            }

            const ratio = Math.min(targetWidth / img.width, targetHeight / img.height);
            const scaledWidth = Math.max(1, Math.round(img.width * ratio));
            const scaledHeight = Math.max(1, Math.round(img.height * ratio));

            canvas.width = scaledWidth;
            canvas.height = scaledHeight;
            ctx.imageSmoothingQuality = 'high';

            ctx.clearRect(0, 0, scaledWidth, scaledHeight);
            ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);

            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Failed to convert canvas to blob'));
                    return;
                }

                const reader = new FileReader();
                reader.onloadend = () => {
                    resolve(reader.result);
                };
                reader.readAsDataURL(blob);
            }, 'image/png');
        };
        // The error Event carries no failure detail and is not an Error instance, so
        // rejecting with it (or with nothing) yields stackless RUM noise — reject
        // with a real Error instead.
        img.onerror = () => reject(new Error('Failed to load image for compression'));
        img.src = base64String;
    });
};

/**
 * Resolves the avatar URL for a Social profile image.
 *
 * Returns the absolute social-assets URL for browser and server callers. Keeping
 * profile images on the asset origin avoids sending `kalshi.com` cookies with
 * public image requests.
 */
export const getIdeasProfileImageUrl = (profileImagePath: string): string => {
    const parsedImagePath = profileImagePath || 'egg';
    return `${config.kalshiSocialPublicS3BucketUrl}/profile-pictures/${parsedImagePath}.webp`;
};

export const hasRealProfileImage = (profileImagePath: string | null | undefined): profileImagePath is string =>
    !!profileImagePath && profileImagePath.toLocaleLowerCase() !== 'egg';
